import { Route, Routes } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { useDispatch, useSelector } from "react-redux";
import { selectIsInvalid, selectIsVerified, selectVerifyingUser, verifyUser } from "./CPL2UserLoginSlice";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { MODULE_NAMES } from "global/constants";
import { useEffect, useState } from "react";
import * as auth from "app/modules/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faRightFromBracket } from "@fortawesome/pro-solid-svg-icons";
import { Style } from "cpl-react-common/Helpers/AlertHelpers";
import { YesNoAlert } from "cpl-react-common/components/Alerts/YesNo";
import CPLPasswordInput from "cpl-react-common/components/Inputs/CPLPasswordInput";

const validateCPL2LoginSchema = Yup.object().shape({
  username: Yup.string().required("Please enter your username"),
  password: Yup.string().required("Please enter your password"),
});

const CPL2UserLoginForm: React.FC = () => {
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const verifyingUser = useSelector(selectVerifyingUser);
  const isInvalid = useSelector(selectIsInvalid);
  const isVerified = useSelector(selectIsVerified);

  const [showLogoutAlert, setShowLogoutAlert] = useState(false);

  useEffect(() => {
    if (isVerified) {
      dispatch(auth.actions.refreshUserAndTokens());
    }
  }, [isVerified, dispatch]);

  const initialValues = {
    username: "",
    password: "",
  };

  const handleLogOutAlertAnswer = (answer: boolean) => {
    if (answer) {
      dispatch(auth.actions.logout());
    } else {
      setShowLogoutAlert(false);
    }
  };

  return (
    <div className="card mb-5 mb-xl-10">
      <div className="card-header">
        <button
          data-test-id="account-link-form__logout-button"
          type="button"
          className="btn p-0 mr-4"
          onClick={() => setShowLogoutAlert(true)}
        >
          <FontAwesomeIcon icon={faRightFromBracket as IconProp} size="xl" />
        </button>
        <div className="card-title m-0">
          <h3 className="fw-bolder m-0">{MODULE_NAMES.CPL2} Log In</h3>
        </div>
        <div />
      </div>
      <div className="card-body border-top p-9">
        <div className="mb-4">
          Please enter the credentials for the {MODULE_NAMES.CPL2} account you would like to link with your new{" "}
          {MODULE_NAMES.CPL3} account
        </div>
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={validateCPL2LoginSchema}
          onSubmit={(values) => {
            thunkDispatch(verifyUser({ username: values.username, password: values.password }));
          }}
        >
          {({ handleSubmit, getFieldProps }) => (
            <Form onSubmit={handleSubmit} className="form">
              <div className="row mb-6">
                <label className="col-lg-4 col-form-label required fw-bold fs-6">Username</label>
                <div className="col-lg-8">
                  <div className="row">
                    <div className="col-lg-12 fv-row">
                      <Field
                        data-test-id="account-link-form__username-input"
                        type="text"
                        className="form-control form-control-lg form-control-solid mb-3 mb-lg-0"
                        {...getFieldProps("username")}
                      />
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          <ErrorMessage name="username" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mb-6">
                <label className="col-lg-4 col-form-label required fw-bold fs-6">Password</label>
                <div className="col-lg-8">
                  <div className="row">
                    <div className="col-lg-12 fv-row">
                      <CPLPasswordInput
                        data-test-id="account-link-form__password-input"
                        id="password"
                        getFieldProps={getFieldProps}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {isInvalid ? (
                <div className="mb-lg-15 alert alert-danger">
                  <div className="alert-text font-weight-bold">{`Your password is incorrect or the user doesn't exist`}</div>
                </div>
              ) : (
                <></>
              )}
              <div className="d-flex justify-content-end" role="group">
                <button
                  data-test-id="account-link-form__link-accounts-button"
                  type="submit"
                  className="btn btn-small btn-primary"
                  disabled={verifyingUser}
                >
                  {!verifyingUser && "Link Accounts"}
                  {verifyingUser && (
                    <span className="indicator-progress" style={{ display: "block" }}>
                      Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  )}
                </button>
              </div>
              <YesNoAlert
                showAlert={showLogoutAlert}
                alertTitle="User Log Out"
                alertText="Are you sure you want to log out?"
                callBack={handleLogOutAlertAnswer}
                style={Style.Warning}
              />
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

const CPL2UserLogin: React.FC = () => {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="AccountLink" element={<CPL2UserLoginForm />} />
        <Route index element={<CPL2UserLoginForm />} />
      </Route>
    </Routes>
  );
};

export default CPL2UserLogin;
